import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth.js';

const mainDomain = import.meta.env.VITE_APP_DOMAIN;

const routes = [
    // Guest
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/auth/Login.vue'),
        meta: { guest: true },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../views/auth/Register.vue'),
        meta: { guest: true, mainDomainOnly: true },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('../views/auth/ForgotPassword.vue'),
        meta: { guest: true },
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: () => import('../views/auth/ResetPassword.vue'),
        meta: { guest: true },
    },
    {
        path: '/verify-email',
        name: 'verify-email',
        component: () => import('../views/auth/VerifyEmail.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/two-factor-challenge',
        name: 'two-factor-challenge',
        component: () => import('../views/auth/TwoFactorChallenge.vue'),
        meta: { guest: true },
    },

    // Landing
    {
        path: '/',
        name: 'landing',
        component: () => import('../views/public/Landing.vue'),
        meta: { guest: true },
    },

    // Authenticated
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('../views/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/surveys',
        name: 'surveys',
        component: () => import('../views/surveys/SurveyList.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/surveys/create',
        name: 'surveys.create',
        component: () => import('../views/surveys/SurveyCreate.vue'),
        meta: { requiresAuth: true, noViewer: true },
    },
    {
        path: '/surveys/:id/edit',
        name: 'surveys.edit',
        component: () => import('../views/surveys/SurveyEdit.vue'),
        meta: { requiresAuth: true, noViewer: true },
    },
    {
        path: '/surveys/:id/responses',
        name: 'surveys.responses',
        component: () => import('../views/surveys/SurveyResponses.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/surveys/:id/share',
        name: 'surveys.share',
        component: () => import('../views/surveys/SurveyShare.vue'),
        meta: { requiresAuth: true },
    },

    // Settings
    {
        path: '/settings/profile',
        name: 'settings.profile',
        component: () => import('../views/settings/Profile.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/settings/domains',
        name: 'settings.domains',
        component: () => import('../views/settings/Domains.vue'),
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: '/settings/members',
        name: 'settings.members',
        component: () => import('../views/settings/Members.vue'),
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: '/settings/organization',
        name: 'settings.organization',
        component: () => import('../views/settings/Organization.vue'),
        meta: { requiresAuth: true, adminOnly: true },
    },

    // Invitations
    {
        path: '/invitations/:token',
        name: 'invitations.accept',
        component: () => import('../views/auth/AcceptInvitation.vue'),
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

export function isMainDomain() {
    return window.location.hostname === mainDomain;
}

router.beforeEach(async (to, from, next) => {
    const auth = useAuthStore();

    if (!auth.initialized && (to.meta.requiresAuth || to.meta.adminOnly)) {
        await auth.fetchUser();
    }

    if (to.meta.mainDomainOnly && !isMainDomain()) {
        return next({ name: 'login' });
    }

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
        return next({ name: 'login' });
    }
    if (to.meta.adminOnly && !auth.isAdmin) {
        return next({ name: 'dashboard' });
    }
    if (to.meta.noViewer && !auth.canManageSurveys) {
        return next({ name: 'surveys' });
    }
    if (to.meta.guest && auth.isAuthenticated) {
        return next({ name: 'dashboard' });
    }
    next();
});

export default router;
